import glossary from './codexGlossary.json';

// Alternate text forms that should resolve to the same codex entry.
// The codex is the source of truth for the canonical name (the key
// here), so plurals and other surface variants live alongside it
// rather than being injected back into the generated JSON.
const ALIASES = {
  Projectile: ['Projectiles'],
};

const glossaryLookup = new Map();
const keywordAbilities = new Map();
const matchableTerms = [];

for (const [key, value] of Object.entries(glossary)) {
  const entry = { keyword: key, description: value };
  glossaryLookup.set(key.toLowerCase(), entry);
  matchableTerms.push({ matchTerm: key, entry });

  for (const alias of ALIASES[key] || []) {
    // Aliases share the canonical entry, so getGlossaryEntry('Projectiles')
    // returns the same { keyword: 'Projectile', description } object.
    // the tooltip header reads the canonical name regardless of the
    // form that appeared in the card text.
    glossaryLookup.set(alias.toLowerCase(), entry);
    matchableTerms.push({ matchTerm: alias, entry });
  }

  if (value.startsWith('Keyword ability') || value.startsWith('Keyword Ability')) {
    const cleanDesc = value.replace(/^Keyword [Aa]bility\.\s*/, '');
    keywordAbilities.set(key.toLowerCase(), { keyword: key, description: cleanDesc });
  }
}

export function extractKeywordAbilities(rulesText) {
  if (!rulesText) return [];

  const found = [];
  const seen = new Set();
  const lines = rulesText.replace(/\r\n/g, '\n').split('\n');

  for (const line of lines) {
    const words = line.match(/\b[A-Z][a-z]+(?:\s\+?\d+)?(?:\s+[A-Z][a-z]+)*\b/g) || [];
    for (const word of words) {
      const key = word.toLowerCase();
      // Try the literal token first ("Ranged 3"), then fall back to the
      // bare keyword by stripping a trailing number ("Ranged"). Card
      // text writes the parameterised form but the codex keys the entry
      // under the unparameterised one.
      const stripped = key.replace(/\s+\+?\d+$/, '');
      const lookupKey = keywordAbilities.has(key)
        ? key
        : (keywordAbilities.has(stripped) ? stripped : null);
      if (!lookupKey || seen.has(lookupKey)) continue;
      seen.add(lookupKey);
      found.push(keywordAbilities.get(lookupKey));
    }
  }

  return found;
}

export function getGlossaryEntry(term) {
  return glossaryLookup.get(term.toLowerCase()) || null;
}

export function findGlossaryTermsInText(text) {
  if (!text) return [];

  const terms = [];
  const seen = new Set();

  for (const { matchTerm, entry } of matchableTerms) {
    const lower = matchTerm.toLowerCase();
    if (lower.length < 3) continue;
    if (keywordAbilities.has(entry.keyword.toLowerCase())) continue;
    if (seen.has(lower)) continue;

    const pattern = new RegExp(`\\b${matchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (pattern.test(text)) {
      seen.add(lower);
      // Emit the matched form so renderTextWithTooltips can split on
      // the exact surface text ("Projectiles") while getGlossaryEntry
      // still resolves it back to the canonical entry.
      terms.push({ keyword: matchTerm, description: entry.description });
    }
  }

  return terms;
}

export { glossary as KEYWORD_GLOSSARY };
